import React from 'react';
import { Box, Button, Divider, Paper, Stack, TextField, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

import { fetchProgressApprovals, addProgressApproval } from './taskprogress.apis';
import { IProgressApproval, ITaskProgress } from './taskprogress.models';

type Props = {
  task?: ITaskProgress;
  onChanged?: () => void;
};

type State = {
  rows: IProgressApproval[];
  approvedrate: number;
  decisionnotes: string;
  saving: boolean;
};

export default class ProgressApprovalPanel extends React.PureComponent<Props, State> {
  state: State = {
    rows: [],
    approvedrate: 0,
    decisionnotes: '',
    saving: false,
  };

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.task?.rid !== this.props.task?.rid) {
      this.load();
      this.setState({
        approvedrate: this.props.task?.apr || 0,
        decisionnotes: '',
      });
    }
  }

  load = async () => {
    if (!this.props.task?.rid) return;
    const rows = await fetchProgressApprovals(this.props.task.rid);
    this.setState({ rows });
  };

  approve = async () => {
    const { task } = this.props;
    if (!task?.rid) return;

    this.setState({ saving: true });

    await addProgressApproval({
      taskprogressid: task.rid,
      validationroleid: 1,
      stateid: 5,
      approvallevel: 1,
      reviewedrate: task.rpr || 0,
      approvedrate: this.state.approvedrate,
      reviewerid: 1,
      decision: 'APPROVED',
      decisionnotes: this.state.decisionnotes,
      extrainfo: {},
      status: 1,
    });

    this.setState({ saving: false, decisionnotes: '' });
    await this.load();
    this.props.onChanged?.();
  };

  render() {
    const { task } = this.props;
    const { rows, approvedrate, decisionnotes, saving } = this.state;

    if (!task) {
      return <Typography color="text.secondary">Select a task to review approvals.</Typography>;
    }

    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <Typography variant="subtitle1">Approval</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Review and approve reported task progress.
        </Typography>

        <Stack spacing={2}>
          <TextField
            label="Approved Progress (%)"
            type="number"
            size="small"
            value={approvedrate}
            onChange={(e) => this.setState({ approvedrate: Number(e.target.value) })}
          />

          <TextField
            label="Decision Notes"
            multiline
            minRows={2}
            value={decisionnotes}
            onChange={(e) => this.setState({ decisionnotes: e.target.value })}
          />

          <Button
            variant="contained"
            startIcon={<CheckIcon />}
            disabled={saving}
            onClick={this.approve}
          >
            Approve
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={1}>
          {rows.map((r) => (
            <Box key={r.rid}>
              <Typography variant="body2">
                <b>{r.dcn}</b> — {r.apd}% by {r.rvn || 'Reviewer'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {r.rvd}
              </Typography>
              {r.dno && (
                <Typography variant="body2" color="text.secondary">
                  {r.dno}
                </Typography>
              )}
            </Box>
          ))}

          {rows.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No approval records yet.
            </Typography>
          )}
        </Stack>
      </Paper>
    );
  }
}